import { cloneDeep } from 'lodash';

interface IDefaultErrorState {
    errors: any;
    errorSummaries: any;
}

export interface PersonalDetails {
    firstName: string;
    lastName: string;
    middleName?: string;
    title: string;
}

export interface Address {
    formattedAddress: string;
    postcode: string;
    state: string;
    suburb: string;
    streetAddress: string;
}

export interface ImpactedAddressType {
    country: Value<string>;
    formattedAddress: Value<string>;
    nswPointId?: Value<string>;
    postcode: Value<string>;
    state: Value<string>;
    streetAddress: Value<string>;
    suburb: Value<string>;
    validated?: Value<boolean>;
}

export interface Value<V> {
    value: V;
    errorMessage?: string;
}

export interface ContactDetails {
    phoneNumber: string;
    emailAddress: string;
    residentialAddress: Address;
}

export interface Eligibility {
    [key: string]: string | object | null | undefined;
}

export interface BusinessDisasterEligibility extends Eligibility {
    business?: {
        abn?: number,
        businessName: string
        abnSuppressed?: boolean
    },
    soleTrader50PercentIncome?: string,
    directDamages?: 'Yes' | 'No',
    operatedDuringDisaster?: 'Yes' | 'No',
    intendToReEstablish?: 'Yes' | 'No',
    insurance?: 'covered' | 'not insured',
    hasFullTimeEmployees?: string,
    employees?: string, // number of employees
    hasAbn?: string,
    impactedAddress?: ImpactedAddressType,
    impactedAddressSameResidential?: 'Yes' | 'No'
}

export type ImpactedAddressMode = 'auto' | 'manual'

export interface PersonalDisasterEligibility extends Eligibility {
    ownerOrRenter?: 'owner' | 'renter',
    ownerOccupierOrInvestor?: 'owner occupier' | 'investor',
    peopleLivingAtAddress?: 'only person' | 'living with others',
    insurance?: 'covered' | 'not insured'
    impactedAddressSource?: 'residential' | 'other',
    impactedAddress?: ImpactedAddressType,
    impactedAddressMode?: ImpactedAddressMode
}

export interface BankDetails {
    accountName: string;
    bsb: string;
    accountNumber: string;
}

export interface Review {
    referenceNumber: string;
    declarations: ReviewDeclarations;
    submitted: boolean;
}

export type ReviewDeclarations = ReviewDeclaration[]

export interface ReviewDeclaration {
    isChecked: boolean,
    value: string,
    label: string
}

/**
 * This is part of the application state *before* the state is mapped for submission.
 * @see RegistrationForm.submissionData.referenceDocuments
 */
export interface IAttachedDocument {
    id: string;
    name: string;
    category?: string;
    documentType?: string;
}

export interface SupportingDocuments extends IDefaultErrorState {
    // TODO: move to keeping them all in the documents collection and
    // separating them by the category field
    documents?: Array<IAttachedDocument>;
}

export interface UserInfo {
    id: string;
    email: string;
}

export interface TransactionDetails {
    correlationId: string;
    poiReferenceNumber: string;
}

export interface ProfileConnectInfo {
    firstName: string;
    familyName: string;
    verifiedName: string;
    email: string;
    mobile: string;
    birthDate: string;
    residentialAddress: any; // create type for address
}

const defaultErrorState: IDefaultErrorState = {
    errors: {},
    errorSummaries: {},
};

interface AppState<E extends Eligibility> {
    transactionDetails: TransactionDetails;
    userInfo: UserInfo;
    profileConnectInfo: ProfileConnectInfo;
    personalDetails: PersonalDetails;
    contactDetails: ContactDetails;
    eligibility: E;
    bankDetails: BankDetails;
    review: Review;
    supportingDocuments: SupportingDocuments;
}

export type ApplicationState = AppState<Eligibility>

export type BusinessDisasterAppState = AppState<BusinessDisasterEligibility>

export type PersonalDisasterAppState = AppState<PersonalDisasterEligibility>

/**
 * Please don't export this - mutations to state could lead to bugs.
 * @see getDefaultInitialApplicationState
 */
const defaultInitialApplicationState: ApplicationState = {
    transactionDetails: { correlationId: '', poiReferenceNumber: '' },
    userInfo: { id: '', email: '' },
    profileConnectInfo: {
        firstName: '',
        birthDate: '',
        email: '',
        familyName: '',
        mobile: '',
        verifiedName: '',
        residentialAddress: {
            formattedAddress: ''
        }
    },
    personalDetails: {
        firstName: 'test',
        lastName: '',
        title: '',
        middleName: ''
    },
    contactDetails: {
        phoneNumber: '',
        emailAddress: '',
        residentialAddress: { formattedAddress: '', state: '', streetAddress: '', suburb: '', postcode: '' }
    },
    eligibility: {
        impactedAddressSource: '',
        impactedAddressMode: 'auto',
    },
    bankDetails: {
        accountName: '',
        bsb: '',
        accountNumber: '',
    },
    review: {
        referenceNumber: '',
        declarations: [],
        submitted: false,
    },
    supportingDocuments: {
        ...defaultErrorState,
        documents: []
    }
};

export const getDefaultInitialApplicationState = () => cloneDeep(defaultInitialApplicationState);
